import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
  MdWarehouse,
  MdLocalFireDepartment,
  MdBuild,
  MdOutlineOpacity,
  MdScale,
  MdAdd,
  MdEdit,
  MdDelete,
  MdFilterList,
  MdViewList,
  MdMenu,
  MdClose,
  MdSupervisorAccount,
  MdPerson,
  MdFactory,
  MdScience,
  MdAssessment,
  MdSearch,
  MdRefresh,
  MdInbox,
} from "react-icons/md";
import AddNewSector from "views/performanceKPI/AddNewSector";
import AddNewKPI from "views/performanceKPI/AddNewKPI";
import { useSectorKpis } from "context/SectorKpiContext";
import { ROLES } from "context/WorkersContext";
import { getSectors, deleteSector } from "api/sectors";
import { getSectorKpis, deleteSectorKpi } from "api/sectorKpis";
import toast from "react-hot-toast";

const PRIMARY = "#0e76a8";
const SECONDARY = "#f27405";

const ICON_MAP = {
  warehouse: MdWarehouse,
  steam: MdLocalFireDepartment,
  build: MdBuild,
  opacity: MdOutlineOpacity,
  scale: MdScale,
  factory: MdFactory,
  science: MdScience,
  assessment: MdAssessment,
};

export default function SectorManagementKPI() {
  const [sectors, setSectors] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(true);
  const [sectorsError, setSectorsError] = useState(null);
  const [activeSectorId, setActiveSectorId] = useState(null);
  const [activeKpiRole, setActiveKpiRole] = useState(ROLES.OPERATOR);
  const [page, setPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [addKpiOpen, setAddKpiOpen] = useState(false);
  const [editKpiData, setEditKpiData] = useState(null);
  const [addSectorOpen, setAddSectorOpen] = useState(false);
  const [editSectorData, setEditSectorData] = useState(null);
  const [deleteSectorData, setDeleteSectorData] = useState(null);
  const [isDeletingSector, setIsDeletingSector] = useState(false);
  const [sectorSearch, setSectorSearch] = useState("");
  const [sectorPage, setSectorPage] = useState(1);
  const sectorsPerPage = 5;
  const [kpisByRole, setKpisByRole] = useState({ [ROLES.OPERATOR]: [], [ROLES.SUPERVISOR]: [] });
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpisError, setKpisError] = useState(null);
  const kpiRows = useMemo(
    () => kpisByRole[activeKpiRole] || [],
    [kpisByRole, activeKpiRole]
  );
  const [deleteKpi, setDeleteKpi] = useState(null); // KPI row to delete
  const [isDeleting, setIsDeleting] = useState(false);
  const { getKpisForSectorRole, addKpi, removeKpi } = useSectorKpis();

  const handleDeleteKpi = async () => {
    if (!deleteKpi) return;
    setIsDeleting(true);
    const toastId = toast.loading("Deleting KPI...");
    try {
      const res = await deleteSectorKpi(deleteKpi.id);
      // Remove locally and refresh list
      removeKpi(activeSectorId, activeKpiRole, deleteKpi.id);
      fetchKpisForSector(activeSectorId);
      const msg = res?.message || res?.data?.message || `"${deleteKpi.name}" deleted successfully`;
      toast.success(msg, { id: toastId });
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || "Failed to delete KPI";
      toast.error(errMsg, { id: toastId });
    } finally {
      setIsDeleting(false);
      setDeleteKpi(null);
    }
  };

  const handleDeleteSector = async () => {
    if (!deleteSectorData) return;
    setIsDeletingSector(true);
    try {
      await deleteSector(deleteSectorData.id);
      toast.success(`"${deleteSectorData.name}" deleted successfully`);
      // If the deleted sector was the active one, clear it so the first remaining sector gets selected
      if (activeSectorId === deleteSectorData.id) {
        setActiveSectorId(null);
      }
      fetchSectors();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to delete sector");
    } finally {
      setIsDeletingSector(false);
      setDeleteSectorData(null);
    }
  };

  const fetchSectors = useCallback(async () => {
    setSectorsLoading(true);
    setSectorsError(null);
    try {
      const result = await getSectors();
      const list = (result.data || []).map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        iconKey: s.icon_key || s.iconKey,
        icon: ICON_MAP[s.icon_key || s.iconKey] || MdWarehouse,
        created_at: s.created_at,
        updated_at: s.updated_at,
      }));
      setSectors(list);
    } catch (err) {
      setSectorsError(err.response?.data?.message || err.message || "Failed to load sectors");
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  // Set active sector when sectors load and none selected
  useEffect(() => {
    if (sectors.length > 0 && !activeSectorId) {
      setActiveSectorId(sectors[0].id);
    }
  }, [sectors, activeSectorId]);

  const mapKpiRow = (row) => ({
    id: row.id,
    name: row.name,
    definition: row.definition ?? row.description ?? "",
    description: row.definition ?? row.description ?? "",
    target: row.target ?? row.target_value ?? "",
  });

  const fetchKpisForSector = useCallback(async (sectorId) => {
    if (sectorId == null) {
      setKpisByRole({ [ROLES.OPERATOR]: [], [ROLES.SUPERVISOR]: [] });
      return;
    }
    setKpisLoading(true);
    setKpisError(null);
    try {
      const [operatorRes, supervisorRes] = await Promise.all([
        getSectorKpis(sectorId, ROLES.OPERATOR),
        getSectorKpis(sectorId, ROLES.SUPERVISOR),
      ]);
      setKpisByRole({
        [ROLES.OPERATOR]: (operatorRes.data || []).map(mapKpiRow),
        [ROLES.SUPERVISOR]: (supervisorRes.data || []).map(mapKpiRow),
      });
    } catch (err) {
      setKpisError(err.response?.data?.message || err.message || "Failed to load KPIs");
      setKpisByRole({ [ROLES.OPERATOR]: [], [ROLES.SUPERVISOR]: [] });
    } finally {
      setKpisLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKpisForSector(activeSectorId);
  }, [activeSectorId, fetchKpisForSector]);

  const fetchKpis = fetchKpisForSector;

  const filteredSectors = useMemo(() => {
    if (!sectorSearch.trim()) return sectors;
    const q = sectorSearch.trim().toLowerCase();
    return sectors.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
    );
  }, [sectors, sectorSearch]);

  const totalSectorPages = Math.max(1, Math.ceil(filteredSectors.length / sectorsPerPage));
  const paginatedSectors = useMemo(() => {
    const start = (sectorPage - 1) * sectorsPerPage;
    return filteredSectors.slice(start, start + sectorsPerPage);
  }, [filteredSectors, sectorPage]);

  const totalKpis = kpiRows.length;
  const pageSize = 5;
  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const syncLockRef = useRef(false);

  const activeSector = sectors.find((s) => s.id === activeSectorId) || sectors[0] || { id: null, name: "—" };

  const closeSidebar = () => setSidebarOpen(false);
  const selectSector = (id) => {
    setActiveSectorId(id);
    closeSidebar();
  };

  const syncScrollFromBody = useCallback(() => {
    if (syncLockRef.current) return;
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (body && header) {
      syncLockRef.current = true;
      header.scrollLeft = body.scrollLeft;
      requestAnimationFrame(() => { syncLockRef.current = false; });
    }
  }, []);

  const syncScrollFromHeader = useCallback(() => {
    if (syncLockRef.current) return;
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (body && header) {
      syncLockRef.current = true;
      body.scrollLeft = header.scrollLeft;
      requestAnimationFrame(() => { syncLockRef.current = false; });
    }
  }, []);

  return (
    <div className="pt-3 px-3 sm:pt-4 sm:px-4 md:pt-6 md:px-0">
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-14rem)] min-h-0 lg:min-h-[500px] overflow-hidden gap-4 lg:gap-0">
        {/* Mobile/tablet: sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={closeSidebar}
            aria-hidden="true"
          />
        )}
        <aside
          className={`fixed top-0 left-0 z-50 h-full w-[min(100%,18rem)] max-w-[18rem] flex-shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col overflow-hidden rounded-none lg:rounded-xl border-0 lg:border transition-transform duration-200 ease-out lg:transition-none
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} 
            lg:relative lg:translate-x-0 lg:w-72 lg:max-w-none lg:mr-6 lg:mt-0 lg:shadow-none`}
        >
          {/* Fixed header — never scrolls */}
          <div className="shrink-0 p-4 sm:p-6 pb-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="flex items-center justify-between gap-3 lg:block lg:mb-0">
              <div>
                <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                  Processing Sectors
                </h2>
                <p className="text-xs text-slate-500 font-medium">Cashew processing workflow.</p>
              </div>
              <button
                type="button"
                onClick={closeSidebar}
                className="lg:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
                aria-label="Close menu"
              >
                <MdClose className="text-xl" />
              </button>
            </div>
            {/* Search */}
            <div className="mt-3 relative">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base pointer-events-none" />
              <input
                type="text"
                value={sectorSearch}
                onChange={(e) => {
                  setSectorSearch(e.target.value);
                  setSectorPage(1);
                }}
                placeholder="Search sectors…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-transparent focus:outline-none"
              />
            </div>
          </div>
          {/* Scrollable sector list */}
          <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 py-4">
            {sectorsLoading ? (
              <p className="text-sm text-slate-500 py-4">Loading sectors…</p>
            ) : sectorsError ? (
              <div className="space-y-2 py-4">
                <p className="text-sm text-red-500">{sectorsError}</p>
                <button
                  type="button"
                  onClick={fetchSectors}
                  className="flex items-center gap-2 text-sm font-bold text-[#0e76a8] hover:underline"
                >
                  <MdRefresh className="text-lg" />
                  Retry
                </button>
              </div>
            ) : (
              <nav className="flex flex-col gap-1.5">
                {paginatedSectors.map((s) => {
                  const Icon = s.icon;
                  const isActive = s.id === activeSectorId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => selectSector(s.id)}
                      className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        isActive
                          ? "bg-[#f27405]/10 text-[#f27405]"
                          : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon className="text-xl shrink-0" />
                        <span className="text-sm font-semibold truncate">{s.name}</span>
                      </div>
                      <span className="text-[10px] font-black text-slate-400 shrink-0">
                        {getKpisForSectorRole(s.id, ROLES.OPERATOR).length + getKpisForSectorRole(s.id, ROLES.SUPERVISOR).length} KPIs
                      </span>
                    </button>
                  );
                })}
                {paginatedSectors.length === 0 && (
                  <p className="text-sm text-slate-500 py-4">
                    {sectors.length === 0
                      ? "No sectors yet. Add one to get started."
                      : "No sectors match your search."}
                  </p>
                )}
              </nav>
            )}
          </div>
          {/* Sector list pagination */}
          {!sectorsLoading && !sectorsError && filteredSectors.length > sectorsPerPage && (
            <div className="shrink-0 px-4 sm:px-6 py-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                Page {sectorPage} of {totalSectorPages}
              </p>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setSectorPage((p) => Math.max(1, p - 1))}
                  disabled={sectorPage <= 1}
                  className="px-2 py-1 rounded text-xs font-bold border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setSectorPage((p) => Math.min(totalSectorPages, p + 1))}
                  disabled={sectorPage >= totalSectorPages}
                  className="px-2 py-1 rounded text-xs font-bold border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {/* Fixed footer */}
          <div className="shrink-0 pt-4 px-4 sm:px-6 pb-4 sm:pb-6 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
            <button
              type="button"
              onClick={() => {
                setEditSectorData(null);
                setAddSectorOpen(true);
              }}
              className="w-full flex items-center justify-center gap-2 rounded-xl h-12 px-4 text-white text-sm font-bold shadow-md hover:opacity-90 transition-all"
              style={{ backgroundColor: SECONDARY }}
            >
              <MdAdd className="text-lg" />
              <span>Add New Sector</span>
            </button>
          </div>
        </aside>

        <AddNewSector
          open={addSectorOpen}
          onClose={() => {
            setAddSectorOpen(false);
            setEditSectorData(null);
          }}
          onSuccess={(payload) => {
            setAddSectorOpen(false);
            setEditSectorData(null);
            fetchSectors();
            if (payload?.id) setActiveSectorId(payload.id);
          }}
          editSector={editSectorData}
        />

        {/* Main content - Sector detail & KPI table */}
        <div className="flex-1 flex flex-col overflow-y-auto min-w-0 min-h-0 lg:min-h-[400px]">
        {/* Mobile: Sectors toggle */}
        <div className="flex items-center gap-3 mb-3 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-semibold"
          >
            <MdMenu className="text-xl" />
            <span>Sectors</span>
          </button>
          <span className="text-slate-400 text-xs font-medium truncate">{activeSector.name}</span>
        </div>

        {/* Breadcrumbs */}
        {/* <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 hidden sm:block">
          USIBRAS GHANA / PROCESSING LINE A
        </p> */}

        {/* Section header */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-start gap-4 mb-4 sm:mb-6">
          <div className="space-y-1 min-w-0">
            <h1 className="text-slate-900 dark:text-white text-xl sm:text-2xl md:text-3xl font-black tracking-tight break-words">
              {activeSector.name}
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm max-w-2xl">
              Management of incoming raw cashew nuts (RCN). Monitor quality metrics and throughput
              efficiency for the initial processing phase.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
            {/* <button
              type="button"
              onClick={() => {
                setEditSectorData(activeSector);
                setAddSectorOpen(true);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors w-full sm:w-auto"
            >
              <MdEdit className="text-lg shrink-0" />
              <span>Edit Sector</span>
            </button>
            <button
              type="button"
              onClick={() => setDeleteSectorData(activeSector)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 dark:border-red-800 bg-white dark:bg-slate-800 rounded-xl text-sm font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors w-full sm:w-auto"
            >
              <MdDelete className="text-lg shrink-0" />
              <span>Delete Sector</span>
            </button> */}
            <button
              type="button"
              onClick={() => {
                setEditKpiData(null);
                setAddKpiOpen(true);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black text-white shadow-lg hover:opacity-90 transition-opacity w-full sm:w-auto"
              style={{ backgroundColor: SECONDARY }}
            >
              <MdAdd className="text-lg shrink-0" />
              <span>Add {activeKpiRole === ROLES.OPERATOR ? "Operator" : "Supervisor"} KPI</span>
            </button>
          </div>
        </div>

        <AddNewKPI
          open={addKpiOpen}
          onClose={() => {
            setAddKpiOpen(false);
            setEditKpiData(null);
          }}
          onSuccess={(payload) => {
            if (editKpiData) {
              // Edit success — just refresh
              setEditKpiData(null);
            } else {
              addKpi(activeSectorId, activeKpiRole, {
                id: payload.id,
                name: payload.name,
                definition: payload.definition ?? payload.description ?? "",
                iconColor: "text-blue-500",
              });
            }
            setAddKpiOpen(false);
            fetchKpisForSector(activeSectorId);
          }}
          sectorName={activeSector?.name}
          sectorId={activeSectorId}
          role={activeKpiRole}
          editKpi={editKpiData}
        />

        {/* KPI section */}
        <div className="rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm flex-1 flex flex-col min-h-0">
          <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <h3 className="font-black text-base sm:text-xl text-slate-900 dark:text-white">
                KPIs by role
              </h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex items-center justify-center rounded-lg sm:rounded-xl size-9 sm:size-10 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-500"
                  title="Filter"
                >
                  <MdFilterList className="text-lg sm:text-xl" />
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center rounded-lg sm:rounded-xl size-9 sm:size-10 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-500"
                  title="View options"
                >
                  <MdViewList className="text-lg sm:text-xl" />
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveKpiRole(ROLES.OPERATOR)}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2 ${
                  activeKpiRole === ROLES.OPERATOR
                    ? "text-white"
                    : "border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                }`}
                style={activeKpiRole === ROLES.OPERATOR ? { backgroundColor: SECONDARY } : {}}
              >
                <MdPerson className="text-lg" />
                Operator KPIs ({(kpisByRole[ROLES.OPERATOR] || []).length})
              </button>
              <button
                type="button"
                onClick={() => setActiveKpiRole(ROLES.SUPERVISOR)}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2 ${
                  activeKpiRole === ROLES.SUPERVISOR
                    ? "text-white"
                    : "border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                }`}
                style={activeKpiRole === ROLES.SUPERVISOR ? { backgroundColor: PRIMARY } : {}}
              >
                <MdSupervisorAccount className="text-lg" />
                Supervisor KPIs ({(kpisByRole[ROLES.SUPERVISOR] || []).length})
              </button>
            </div>
          </div>

          {/* Mobile: KPI cards */}
          <div className="flex-1 overflow-y-auto md:hidden p-4 space-y-4">
            {kpisLoading ? (
              <p className="text-sm text-slate-500 py-4">Loading KPIs…</p>
            ) : kpisError ? (
              <div className="space-y-2 py-4">
                <p className="text-sm text-red-500">{kpisError}</p>
                <button
                  type="button"
                  onClick={() => fetchKpisForSector(activeSectorId)}
                  className="flex items-center gap-2 text-sm font-bold text-[#0e76a8] hover:underline"
                >
                  <MdRefresh className="text-lg" />
                  Retry
                </button>
              </div>
            ) : kpiRows.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center py-16 px-4">
                <MdInbox className="text-6xl text-slate-300 dark:text-slate-600 mb-3" aria-hidden />
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No KPIs yet</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Add one to get started</p>
              </div>
            ) : (
            <>
            {kpiRows.map((row) => (
              <div
                key={row.id}
                className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50/30 dark:bg-slate-800/30"
              >
                <div className="flex justify-between items-start gap-3 mb-2">
                  <h4 className="font-bold text-sm text-slate-900 dark:text-white">{row.name}</h4>
                  {/* <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setEditKpiData(row);
                        setAddKpiOpen(true);
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-[#0e76a8]"
                      aria-label="Edit"
                    >
                      <MdEdit className="text-lg" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteKpi(row)}
                      className="p-1.5 rounded-lg text-slate-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600"
                      aria-label="Delete"
                    >
                      <MdDelete className="text-lg" />
                    </button>
                  </div> */}
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                  {row.definition || row.description}
                </p>
              </div>
            ))}
            </>
            )}
          </div>

          {/* Tablet/desktop: KPI table — split header/body so rows never overlay header */}
          <div className="flex-1 min-h-0 hidden md:flex md:flex-col md:overflow-hidden">
            {kpisLoading ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <p className="text-sm text-slate-500">Loading KPIs…</p>
              </div>
            ) : kpisError ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 gap-3">
                <p className="text-sm text-red-500 text-center">{kpisError}</p>
                <button
                  type="button"
                  onClick={() => fetchKpisForSector(activeSectorId)}
                  className="flex items-center gap-2 text-sm font-bold text-[#0e76a8] hover:underline"
                >
                  <MdRefresh className="text-lg" />
                  Retry
                </button>
              </div>
            ) : (
            <>
            <div
              ref={headerScrollRef}
              onScroll={syncScrollFromHeader}
              className="overflow-x-auto overflow-y-hidden shrink-0 border-b-2 border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800"
            >
              <table className="w-full text-sm min-w-[600px] table-fixed lg:table-auto border-collapse kpi-table">
                <thead>
                  <tr>
                    <th className="px-4 sm:px-6 lg:px-8 py-3.5 sm:py-4 text-left text-[10px] sm:text-xs font-black uppercase tracking-widest whitespace-nowrap align-middle min-w-[120px] w-[30%] lg:w-auto text-slate-500">
                      Metric
                    </th>
                    <th className="px-4 sm:px-6 lg:px-8 py-3.5 sm:py-4 text-left text-[10px] sm:text-xs font-black uppercase tracking-widest align-middle hidden lg:table-cell min-w-[200px] w-[55%] lg:w-auto text-slate-500">
                      Definition
                    </th>
                    <th className="px-4 sm:px-6 lg:px-8 py-3.5 sm:py-4 text-left text-[10px] sm:text-xs font-black uppercase tracking-widest align-middle min-w-[120px] w-[15%] lg:w-auto text-slate-500">
                      Target
                    </th>
                    <th className="px-4 sm:px-6 lg:px-8 py-3.5 sm:py-4 text-right text-[10px] sm:text-xs font-black uppercase tracking-widest whitespace-nowrap align-middle min-w-[80px] w-[15%] lg:w-auto text-slate-500">
                      Actions
                    </th>
                  </tr>
                </thead>
              </table>
            </div>
            <div
              ref={bodyScrollRef}
              onScroll={syncScrollFromBody}
              className="overflow-auto flex-1 min-h-0"
            >
              <table className="w-full text-sm min-w-[600px] table-fixed lg:table-auto border-collapse kpi-table">
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {kpiRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-16 text-center align-middle">
                        <div className="flex flex-col items-center justify-center">
                          <MdInbox className="text-6xl text-slate-300 dark:text-slate-600 mb-3" aria-hidden />
                          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No KPIs yet</p>
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Add one to get started</p>
                        </div>
                      </td>
                    </tr>
                  ) : kpiRows.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 align-middle font-semibold text-slate-900 dark:text-white text-sm min-w-[120px] w-[30%] lg:w-auto">
                        {row.name}
                      </td>
                      <td className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 align-top text-slate-600 dark:text-slate-300 text-sm leading-relaxed hidden lg:table-cell min-w-[200px] max-w-[360px] w-[55%] lg:w-auto">
                        <span className="block py-0.5">{row.definition || row.description}</span>
                      </td>
                      <td className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 align-middle text-slate-700 dark:text-white text-sm min-w-[120px] w-[15%] lg:w-auto">
                        <span className="inline-block truncate max-w-[12rem]">{row.target || "—"}</span>
                      </td>
                      <td className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 align-middle text-right min-w-[80px] w-[15%] lg:w-auto">
                        <div className="flex items-center justify-end gap-1 sm:gap-2 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              setEditKpiData(row);
                              setAddKpiOpen(true);
                            }}
                            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-[#0e76a8] transition-colors"
                            title="Edit"
                          >
                            <MdEdit className="text-base sm:text-lg" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteKpi(row)}
                            className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <MdDelete className="text-base sm:text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}
          </div>

          <div className="p-3 sm:p-4 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row flex-wrap justify-between items-stretch sm:items-center gap-3 bg-slate-50/50 dark:bg-slate-800/30">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest order-2 sm:order-1 text-center sm:text-left">
              Showing {pageSize * (page - 1) + 1}-{Math.min(pageSize * page, totalKpis)} of{" "}
              {totalKpis} {activeSector.name} KPIs
            </p>
            <div className="flex gap-2 order-1 sm:order-2 justify-center sm:justify-end">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex-1 sm:flex-initial px-4 py-2 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page * pageSize >= totalKpis}
                className="flex-1 sm:flex-initial px-4 py-2 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteKpi && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => !isDeleting && setDeleteKpi(null)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <MdDelete className="text-2xl text-red-600" />
                </div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white mb-1">
                  Delete KPI
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Are you sure you want to delete{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    "{deleteKpi.name}"
                  </span>
                  ? This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 px-6 pb-6">
                <button
                  type="button"
                  onClick={() => setDeleteKpi(null)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteKpi}
                  disabled={isDeleting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 shadow-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <MdDelete className="text-lg" />
                  {isDeleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Sector Confirmation Modal */}
      {deleteSectorData && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => !isDeletingSector && setDeleteSectorData(null)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <MdDelete className="text-2xl text-red-600" />
                </div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white mb-1">
                  Delete Sector
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Are you sure you want to delete{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    "{deleteSectorData.name}"
                  </span>
                  ? All KPIs associated with this sector will also be removed. This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 px-6 pb-6">
                <button
                  type="button"
                  onClick={() => setDeleteSectorData(null)}
                  disabled={isDeletingSector}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSector}
                  disabled={isDeletingSector}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 shadow-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <MdDelete className="text-lg" />
                  {isDeletingSector ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
